import type { AccordionItemData } from './Accordion'

export const modules: AccordionItemData[] = [
  {
    title:
      '🏐 VOL. 1 – Technique and Performance: Serve, Spike, Block, and Dig (55 pages)',
    content: [
      'Introduction: why clean technique and shoulder health are not separate goals',
      'Functional anatomy of the overhead hitting motion (kinetic chain: legs, hips, trunk, shoulder, arm)',
      'Serve: float vs. jump serve mechanics — the point of highest shoulder stress and its link to real power',
      'Spike: arm-swing sequencing, jump timing, and the error that overloads the rotator cuff',
      'Block: repeated vertical jump mechanics and safe landing at the net',
      'Reception and defense (dig): low posture and its link to repetitive low back strain',
      'Setting: wrist and finger mechanics, an under-recognized overuse area',
      'Footwork: lateral movement and stopping — the hidden base beneath every other skill',
      'Video self-assessment: 6 technical checkpoints per fundamental',
      'Most common technical errors by position (setter, opposite, middle blocker, outside hitter, libero)',
    ],
  },
  {
    title: '🛡️ VOL. 2 – Injury Prevention by Specific Movement (55 pages)',
    content: [
      "Introduction: why the shoulder and ankle concentrate most of the sport's injuries",
      "Volleyball shoulder: impingement from repeated serving/spiking, the sport's #1 overuse injury",
      'Shoulder prevention protocol: rotator cuff and scapular stability',
      "Ankle: sprain from landing on an opponent's foot after a block",
      'Ankle prevention protocol: proprioception and stabilizer strength',
      `Knee: patellar tendinitis ("jumper's knee") from repeated spike and block jumping`,
      'Knee prevention protocol: neuromuscular control on landing',
      'Low back: overload from repeated reception posture',
      'Fingers: jamming from ball contact, management protocol',
      'Quick-reference table: pain area → likely cause → matching protocol',
      'Warning signs: when pain is normal training soreness and when to stop',
    ],
  },
  {
    title:
      '⚡ VOL. 3 – 12-Week Strength, Jump, and Conditioning Program (55 pages)',
    content: [
      'Periodization for the amateur/school player',
      'Phase 1 (Weeks 1-4): strength base and shoulder/ankle stability',
      'Phase 2 (Weeks 5-8): jump power (vertical jump) built on safe landing mechanics',
      'Phase 3 (Weeks 9-12): sport-specific conditioning (repeated jumping, fast transitions)',
      'Week-by-week routines with sets, reps, and progression',
      'Dynamic pre-practice/match warm-up (12-15 min), with shoulder activation emphasis',
      'Core work: the hidden base of hitting power and low back protection',
      'Post-match cooldown and recovery',
      'How to measure real progress (jump height, hitting speed — not just how it feels)',
    ],
  },
  {
    title:
      '🧠 VOL. 4 – Psychology of the Player: The Mind in Every Rotation (45 pages)',
    content: [
      'Introduction: why volleyball is a sport of visible, immediate errors in front of the entire team',
      'Managing pressure on the decisive serve',
      'Pre-serve routine: building a ritual that cuts anxiety',
      'Focus and mental reset after a mistake',
      "Psychological momentum: cutting an opponent's run and not carrying your own bad stretch forward",
      'Technical confidence vs. in-match confidence',
      'Communicating under pressure: talking to your team without adding more pressure',
      'Performance anxiety in young players: recognizing it and working through it',
      'The mental return after an injury',
      'Applied visualization: a simple pre-match and pre-serve protocol',
    ],
  },
  {
    title:
      '🌱 VOL. 5 – Youth Volleyball and Return to Play: A Guide for Developing Athletes and Recovery (45 pages)',
    content: [
      'Why a growing body responds differently to the same jump training',
      'Sinding-Larsen-Johansson and Osgood-Schlatter disease',
      'Early specialization: risks of playing year-round without rest',
      'Safe training load by age (reference guide)',
      'Guide for parents and coaches: supporting without overprotecting or over-pressuring',
      'General principles of progressive return to sport',
      'Return protocol: shoulder',
      'Return protocol: ankle and knee',
      'Green/yellow/red progression checklist',
      'When return to play requires medical clearance',
    ],
  },
]

export const bonuses: AccordionItemData[] = [
  {
    title: '📊 Bonus 1 – Risk & Performance Calculator (Excel, interactive)',
    content: [
      'Connects to: Volume 1 and Volume 2',
      'User enters age, weekly training/match volume, current shoulder or ankle discomfort, and main goal (health vs. jump/hitting power)',
      'Calculates a Risk Score and returns the priority protocol from Volume 2',
      'Adds a performance note tied to Volume 1',
    ],
  },
  {
    title: '🗓️ Bonus 2 – Your First 4 Weeks (PDF)',
    content: [
      'Connects to: all volumes',
      'Day-by-day roadmap for the first month',
      'Sequences which section of which volume to use and when, based on the Bonus 1 result',
      'Orders when to start using Bonuses 3, 4, and 5',
    ],
  },
  {
    title: '📈 Bonus 3 – Safe Training Load Planner (Excel, interactive)',
    content: [
      'Connects to: Volume 2, Volume 3, and Volume 5',
      '20-week weekly log: matches, practices, and open-gym/club sessions',
      'Weighted-volume formula (matches count more than open-gym)',
      'Automatic conditional OK / OVER LIMIT flag by age group, including under-13',
    ],
  },
  {
    title: '🎯 Bonus 4 – Serve Routine & Pressure Reset (PDF)',
    content: [
      'Connects to: Volume 4',
      'Two routines: one for the serve under pressure, one for the mental reset after a mid-point error',
      'Direct application of the sports psychology volume',
    ],
  },
  {
    title:
      '📉 Bonus 5 – 12-Week Progression Tracker (Excel, interactive, with chart)',
    content: [
      'Connects to: Volume 3',
      'Editable companion to the 12-week program',
      'User logs weekly: strength, vertical jump, and shoulder/ankle discomfort',
      'Automatic line chart showing the trend of all three metrics together',
    ],
  },
]

export const testimonials = [
  {
    quote:
      'I stopped guessing through shoulder soreness. My serve feels cleaner and I finally know what to do between tournaments.',
    name: 'Megan R.',
    meta: 'Outside hitter, 22 · Austin, TX',
  },
  {
    quote:
      'The landing work changed the way I train. I feel quicker at the net and much more confident after my ankle scare.',
    name: 'Jordan P.',
    meta: 'Middle blocker, 19 · Columbus, OH',
  },
  {
    quote:
      'As a club libero, the warm-up and footwork pieces are exactly what I needed. Clear, practical, and easy to follow.',
    name: 'Alyssa K.',
    meta: 'Libero, 17 · San Diego, CA',
  },
  {
    quote:
      'The 12-week plan gives me structure without taking over my life. My jump has improved and my knees are happier.',
    name: 'Derek M.',
    meta: 'Opposite, 27 · Denver, CO',
  },
  {
    quote:
      'I bought it for my daughter and we use the youth load guidance every week. It is such a helpful coaching conversation starter.',
    name: 'Tanya W.',
    meta: 'Volleyball parent, 41 · Raleigh, NC',
  },
]

export const socialProof = [
  ['Olivia M.', 'Phoenix, AZ'],
  ['Ethan R.', 'Nashville, TN'],
  ['Kayla S.', 'Tampa, FL'],
  ['Marcus B.', 'Chicago, IL'],
  ['Sofia L.', 'Seattle, WA'],
]
